import { Camera, Plus, Star, Trash2, X } from "lucide-react";
import { UrduInput } from "../../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../../Components/Shared/ImageSeoFields";
import {
  hexToColorName,
  colorNameToHex,
} from "../../utils/colorHelpers";

export default function ColorCard({
  form,
  actualIndex,
  colorIndexInVariant,
  colorsInVariantLength,
  layout = "multi",
}) {
  const {
    formData,
    validationErrors,
    fileInputRefs,
    textureImageInputRefs,
    handleAddImageClick,
    handleColorImageChange,
    handleRemoveColorImage,
    handleSetPrimaryColorImage,
    handleVariantChange,
    clearFieldErrors,
    handleTextureImageChange,
    handleAddTextureImageClick,
    handleRemoveTextureImage,
    onExistingColorImagePositionChange,
    onNewColorImagePositionChange,
    onExistingColorImageSeoChange,
    onNewColorImageSeoChange,
    removeColor,
  } = form;

  const variant = formData.colors[actualIndex];
  if (!variant) return null;

  const allImages = [
    ...(variant.existingImages || []),
    ...(variant.newImageFiles || []),
  ];

  const colorHex =
    variant.colorCode ||
    colorNameToHex(variant.color) ||
    "#9ca3af";

  const imageError =
    validationErrors[`variant_${actualIndex}_images`] ||
    validationErrors[`variant_${actualIndex}_color_or_image`];

  const renderImageTile = (file, imgIndex) => {
    const isExisting = imgIndex < (variant.existingImages?.length || 0);
    const newFileIndex = imgIndex - (variant.existingImages?.length || 0);
    let src = "/assets/logo.png";
    if (isExisting) {
      if (typeof file === "string") src = file;
      else if (file?.url) src = file.url;
      else if (file instanceof File) src = URL.createObjectURL(file);
    } else if (file instanceof File) {
      src = URL.createObjectURL(file);
    } else if (typeof file === "string") {
      src = file;
    }

    const isPrimaryImage = isExisting
      ? typeof file === "object" && !!file?.isPrimary
      : variant.newImagePrimaryIndex === newFileIndex;
    const positionValue = isExisting
      ? typeof file === "object" &&
        file?.position != null &&
        Number(file.position) >= 1
        ? file.position
        : ""
      : variant.newImagePositions?.[newFileIndex] ?? "";

    return (
      <div
        key={imgIndex}
        className={`relative flex flex-col border rounded-lg overflow-hidden bg-white group ${
          isPrimaryImage
            ? "border-brand-primary ring-1 ring-brand-primary/40"
            : "border-[var(--pf-border)]"
        }`}
      >
        <div className="relative w-full aspect-square bg-gray-50">
          <img
            src={src}
            alt="preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = "/assets/logo.png";
              e.target.onerror = null;
            }}
          />
          <div className="absolute top-1.5 right-1.5 flex items-center gap-1 z-10">
            <button
              type="button"
              onClick={() =>
                handleSetPrimaryColorImage(
                  actualIndex,
                  isExisting ? imgIndex : newFileIndex,
                  isExisting,
                )
              }
              className={`h-6 w-6 rounded-full flex items-center justify-center shadow-sm transition-opacity ${
                isPrimaryImage
                  ? "bg-brand-primary text-white opacity-100"
                  : "bg-white/95 text-gray-600 opacity-0 group-hover:opacity-100 hover:bg-brand-primary/10 hover:text-brand-primary"
              }`}
              title={isPrimaryImage ? "Primary image" : "Set as primary"}
              aria-label={isPrimaryImage ? "Primary image" : "Set as primary"}
            >
              <Star
                size={12}
                fill={isPrimaryImage ? "currentColor" : "none"}
              />
            </button>
            <button
              type="button"
              className="h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() =>
                handleRemoveColorImage(
                  actualIndex,
                  isExisting ? imgIndex : newFileIndex,
                  isExisting,
                )
              }
              aria-label="Remove image"
            >
              <X size={12} />
            </button>
          </div>
          {!isPrimaryImage && (
            <input
              type="text"
              inputMode="numeric"
              pattern="[1-9][0-9]*"
              value={positionValue}
              onChange={(e) => {
                if (isExisting) {
                  onExistingColorImagePositionChange(
                    actualIndex,
                    imgIndex,
                    e.target.value,
                  );
                } else {
                  onNewColorImagePositionChange(
                    actualIndex,
                    newFileIndex,
                    e.target.value,
                  );
                }
              }}
              className="absolute bottom-1.5 left-1.5 w-10 px-1 py-0.5 text-[10px] text-center bg-white/95 border border-gray-200 rounded shadow-sm z-10"
              placeholder="#"
              title="Display position"
            />
          )}
          {isPrimaryImage && (
            <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-brand-primary text-white rounded z-10">
              Primary
            </span>
          )}
        </div>
        <div className="p-2.5 bg-white border-t border-gray-100">
          <ImageSeoFields
            stacked
            imageAlt={
              isExisting
                ? (typeof file === "object" ? file?.image_alt : "") || ""
                : variant.newImageAlts?.[newFileIndex] || ""
            }
            imageTitle={
              isExisting
                ? (typeof file === "object" ? file?.image_title : "") || ""
                : variant.newImageTitles?.[newFileIndex] || ""
            }
            onAltChange={(value) => {
              if (isExisting) {
                onExistingColorImageSeoChange(
                  actualIndex,
                  imgIndex,
                  "image_alt",
                  value,
                );
              } else {
                onNewColorImageSeoChange(
                  actualIndex,
                  newFileIndex,
                  "image_alt",
                  value,
                );
              }
            }}
            onTitleChange={(value) => {
              if (isExisting) {
                onExistingColorImageSeoChange(
                  actualIndex,
                  imgIndex,
                  "image_title",
                  value,
                );
              } else {
                onNewColorImageSeoChange(
                  actualIndex,
                  newFileIndex,
                  "image_title",
                  value,
                );
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      data-variant-index={actualIndex}
      className="rounded-lg border border-[var(--pf-border)] bg-white p-3.5 space-y-3.5"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-brand-primary mb-1">
            Step 1 · Color
          </p>
          {variant.color ? (
            <span className="pf-color-chip">
              <span
                className="pf-color-dot"
                style={{ backgroundColor: colorHex }}
              />
              {variant.color}
            </span>
          ) : (
            <span className="text-sm font-bold text-gray-800">
              Color{" "}
              {layout === "single"
                ? actualIndex + 1
                : colorIndexInVariant + 1}
            </span>
          )}
          <p className="pf-step-hint">Pick a color name or hex code</p>
        </div>
        {colorsInVariantLength > 1 && (
          <button
            type="button"
            onClick={() => removeColor(actualIndex)}
            className="pf-btn-remove-variant"
          >
            <Trash2 size={14} strokeWidth={2.25} />
            Remove Color
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="pf-label">Color</label>
          <div className="flex gap-2 items-center">
            <input
              type="color"
              value={(() => {
                if (variant.colorCode) return variant.colorCode;
                const hexFromName = colorNameToHex(variant.color);
                if (hexFromName) return hexFromName;
                if (
                  variant.color &&
                  variant.color.match(/^#[0-9A-Fa-f]{6}$/i)
                ) {
                  return variant.color.toUpperCase();
                }
                return "#000000";
              })()}
              onChange={(e) => {
                const hexColor = e.target.value.toUpperCase();
                const colorName = hexToColorName(hexColor);
                const finalColor = colorName || hexColor;
                handleVariantChange(actualIndex, "color", finalColor);
                handleVariantChange(actualIndex, "colorCode", hexColor);
                clearFieldErrors([
                  `colors.${actualIndex}.color`,
                  `colors.${actualIndex}.color_or_image`,
                  `colors.${actualIndex}.variant_image`,
                ]);
              }}
              className="w-10 h-9 border border-gray-300 rounded cursor-pointer shrink-0"
              title="Pick a color"
            />
            <input
              type="text"
              value={variant.color || ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                handleVariantChange(actualIndex, "color", inputValue);

                let hexCode = null;
                if (inputValue.match(/^#[0-9A-Fa-f]{6}$/i)) {
                  hexCode = inputValue.toUpperCase();
                } else {
                  hexCode = colorNameToHex(inputValue);
                }

                if (hexCode) {
                  handleVariantChange(actualIndex, "colorCode", hexCode);
                } else if (!inputValue) {
                  handleVariantChange(actualIndex, "colorCode", "#000000");
                }

                clearFieldErrors([
                  `colors.${actualIndex}.color`,
                  `colors.${actualIndex}.color_or_image`,
                  `colors.${actualIndex}.variant_image`,
                ]);
              }}
              placeholder="e.g. Grey"
              className={`pf-input flex-1 ${
                validationErrors[`variant_${actualIndex}_color`] ||
                validationErrors[`variant_${actualIndex}_color_or_image`]
                  ? "is-error"
                  : ""
              }`}
            />
          </div>
          {validationErrors[`variant_${actualIndex}_color`] && (
            <p className="text-red-500 text-xs mt-1 font-semibold">
              {validationErrors[`variant_${actualIndex}_color`]}
            </p>
          )}
          {validationErrors[`variant_${actualIndex}_color_or_image`] && (
            <p className="text-red-500 text-xs mt-1 font-semibold">
              {validationErrors[`variant_${actualIndex}_color_or_image`]}
            </p>
          )}
        </div>
        <div>
          <UrduInput
            label="رنگ"
            name={`color_ur_${actualIndex}`}
            value={variant.color_ur || ""}
            onChange={(e) =>
              handleVariantChange(actualIndex, "color_ur", e.target.value)
            }
            className="mt-0"
          />
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-800">
            <input
              type="checkbox"
              checked={!!variant.texture}
              onChange={(e) =>
                handleVariantChange(actualIndex, "texture", e.target.checked)
              }
              className="rounded border-gray-300 accent-brand-primary w-4 h-4"
            />
            Has Texture
          </label>
          {variant.texture && (
            <input
              type="text"
              value={variant.textureCode ?? ""}
              onChange={(e) =>
                handleVariantChange(actualIndex, "textureCode", e.target.value)
              }
              placeholder="Texture code (optional)"
              className="pf-input flex-1 min-w-[140px] py-1.5 text-xs"
            />
          )}
        </div>

        {variant.texture && (
          <>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={(el) => {
                textureImageInputRefs.current[actualIndex] = el;
              }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleTextureImageChange(actualIndex, file);
                e.target.value = "";
              }}
            />

            {variant.textureImageFile || variant.existingTextureImage ? (
              <div className="pf-cover-preview">
                <img
                  src={
                    variant.textureImageFile
                      ? URL.createObjectURL(variant.textureImageFile)
                      : variant.existingTextureImage
                  }
                  alt="Texture preview"
                  onError={(e) => {
                    e.target.src = "/assets/logo.png";
                    e.target.onerror = null;
                  }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-800 truncate">
                    {variant.textureImageFile?.name || "Existing texture image"}
                  </p>
                  <p className="text-xs font-semibold text-gray-500 mt-0.5">
                    Required size: 450 × 450 px
                  </p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => handleAddTextureImageClick(actualIndex)}
                      className="text-xs font-bold text-brand-primary hover:underline"
                    >
                      Change Texture
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveTextureImage(actualIndex)}
                      className="text-xs font-bold text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <ImageSeoFields
                  imageAlt={variant.textureImageAlt || ""}
                  imageTitle={variant.textureImageTitle || ""}
                  onAltChange={(value) =>
                    handleVariantChange(actualIndex, "textureImageAlt", value)
                  }
                  onTitleChange={(value) =>
                    handleVariantChange(actualIndex, "textureImageTitle", value)
                  }
                  className="mt-2"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => handleAddTextureImageClick(actualIndex)}
                className="pf-upload-slot py-5"
              >
                <Camera size={20} strokeWidth={1.5} />
                <span className="pf-upload-title">Upload Texture Image</span>
                <span className="pf-upload-hint">
                  Required dimensions: 450 × 450 px
                  <br />
                  JPG or PNG · Square crop recommended
                </span>
              </button>
            )}
          </>
        )}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-brand-primary">
              Step 2 · Images
            </p>
            <label className="pf-label mb-0 mt-0.5">
              {layout === "single" ? "Product Gallery" : "Variant Gallery"}
              <span className="ml-1.5 font-semibold normal-case tracking-normal text-gray-500">
                (450 × 450 px)
              </span>
            </label>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Hover an image and click the star to set it as primary
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleAddImageClick(actualIndex)}
            className="pf-btn-cta pf-btn-cta-primary"
            disabled={allImages.length >= 9999}
          >
            <Camera size={15} strokeWidth={2} />
            Add Image
          </button>
        </div>

        <input
          type="file"
          multiple
          accept="image/*"
          style={{ display: "none" }}
          ref={(el) => {
            fileInputRefs.current[actualIndex] = el;
          }}
          onChange={(e) => handleColorImageChange(actualIndex, e.target.files)}
        />

        {allImages.length === 0 ? (
          <button
            type="button"
            onClick={() => handleAddImageClick(actualIndex)}
            className={`pf-upload-slot py-7 ${imageError ? "is-error" : ""}`}
          >
            <Camera size={22} strokeWidth={1.5} />
            <span className="pf-upload-title">Click to Add Image</span>
            <span className="pf-upload-hint">
              Required dimensions: 450 × 450 px
              <br />
              JPG or PNG · You can add multiple images
            </span>
          </button>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {allImages.map((file, imgIndex) =>
              renderImageTile(file, imgIndex),
            )}
            {allImages.length < 9999 && (
              <button
                type="button"
                onClick={() => handleAddImageClick(actualIndex)}
                className={`pf-upload-slot min-h-[180px] ${
                  imageError ? "is-error" : ""
                }`}
              >
                <Plus size={18} strokeWidth={1.75} />
                <span className="pf-upload-title">Add Image</span>
                <span className="pf-upload-hint">450 × 450</span>
              </button>
            )}
          </div>
        )}

        {imageError && (
          <p className="text-red-500 text-xs mt-2 font-semibold">{imageError}</p>
        )}
      </div>
    </div>
  );
}
